import { apiClient } from '@/lib/api-client';
import type { RespostaApi } from '@/types/api';
import type {
  LoginPayload,
  RegistroPayload,
  VerificarContaPayload,
  RecuperarSenhaPayload,
  RenovarTokenPayload,
} from '@/types/auth';

const API_URL = process.env.NEXT_PUBLIC_API_URL!;

/**
 * Faz POST/GET com fetch direto (sem auth) e retorna RespostaApi.
 * Quando o backend devolve JSON com sucesso/mensagem, repassa intacto
 * para o caller decidir o que fazer (mesmo em 4xx).
 * Quando nao consegue parsear JSON, lanca Error com mensagem amigavel.
 */
async function fetchRespostaApi(
  url: string,
  init: RequestInit,
): Promise<RespostaApi> {
  let response: Response;
  try {
    response = await fetch(url, init);
  } catch {
    throw new Error('Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.');
  }

  const text = await response.text().catch(() => '');
  let parsed: RespostaApi | null = null;
  if (text) {
    try {
      parsed = JSON.parse(text) as RespostaApi;
    } catch {
      parsed = null;
    }
  }

  // Se o backend retornou RespostaApi (mesmo que sucesso=false), repassa
  if (parsed && typeof parsed.sucesso === 'boolean') {
    return parsed;
  }

  // Sem RespostaApi parseavel: gera mensagem amigavel por status
  let mensagem: string;
  if (response.status === 0 || response.status >= 502) {
    mensagem = 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.';
  } else if (response.status === 504) {
    mensagem = 'O servidor demorou para responder. Tente novamente em instantes.';
  } else if (response.status >= 500) {
    mensagem = 'Ocorreu um erro no servidor. Tente novamente mais tarde.';
  } else if (!response.ok) {
    mensagem = `Erro ${response.status}: ${response.statusText || 'Falha na requisição'}`;
  } else {
    mensagem = 'Resposta inválida do servidor.';
  }
  throw new Error(mensagem);
}
/**
 * TODO: RISCO DE SEGURANÇA — Esta API key é exposta no bundle client-side via NEXT_PUBLIC_.
 * Como o projeto usa `output: 'export'` (static), não é possível usar API routes server-side.
 * A solução ideal é criar um endpoint proxy no backend .NET (ex: /api/PortalContador/ConsultarCNPJ)
 * que faça a chamada à API de CNPJ internamente, e remover esta variável de ambiente do frontend.
 */
const CNPJ_API_KEY = process.env.NEXT_PUBLIC_CNPJ_API_KEY!;

/**
 * Realiza login do contador.
 */
export async function logarContador(
  email: string,
  senha: string
): Promise<RespostaApi> {
  const payload: LoginPayload = { email, senha };
  return fetchRespostaApi(`${API_URL}/api/PortalContador/Login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

/**
 * Verifica a conta do contador com o codigo de 6 digitos.
 */
export async function verificarConta(
  email: string,
  codigo: number
): Promise<RespostaApi> {
  const payload: VerificarContaPayload = { email, codigo };
  return fetchRespostaApi(`${API_URL}/api/PortalContador/VerificarConta`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

/**
 * Envia um novo codigo de verificacao para o email do contador.
 */
export async function enviarNovoCodigo(email: string): Promise<RespostaApi> {
  return fetchRespostaApi(`${API_URL}/api/PortalContador/EnviarNovoCodigo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

/**
 * Valida o codigo de 6 digitos SEM alterar a conta.
 *
 * Serve para barrar um codigo errado ainda na tela do codigo, em vez de deixar o erro
 * aparecer so na tela de redefinicao de senha.
 *
 * O endpoint recebe `[FromBody] string`, entao o corpo e a string em JSON ("123456"),
 * nao um objeto.
 */
export async function verificarCodigo(codigo: string): Promise<RespostaApi> {
  return fetchRespostaApi(`${API_URL}/api/PortalContador/VerificarCodigo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(codigo),
  });
}

/**
 * Redefine a senha do contador usando o token de recuperacao.
 */
export async function recuperarSenha(
  token: string,
  senha: string,
  confirmacaoSenha: string
): Promise<RespostaApi> {
  const payload: RecuperarSenhaPayload = {
    token,
    senha,
    confirmacao_senha: confirmacaoSenha,
  };
  return fetchRespostaApi(`${API_URL}/api/PortalContador/RecuperarSenha`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

/**
 * Renova o access token usando o refresh token.
 */
export async function renovarToken(
  refreshToken: string
): Promise<RespostaApi> {
  const payload: RenovarTokenPayload = { refresh_token: refreshToken };
  return fetchRespostaApi(`${API_URL}/api/PortalContador/RenovarToken`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

/**
 * Realiza logout do contador, invalidando o refresh token no servidor.
 */
export async function logoutContador(token: string): Promise<void> {
  // Logout silencioso: nao trava o usuario se backend falhar
  try {
    await fetch(`${API_URL}/api/PortalContador/Logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({}),
    });
  } catch {
    // ignorar — token sera removido localmente de qualquer forma
  }
}

/**
 * Registra um novo contador.
 */
export async function registrarContador(
  payload: RegistroPayload
): Promise<RespostaApi> {
  return fetchRespostaApi(`${API_URL}/api/PortalContador/Registro`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

/**
 * Consulta dados de uma empresa pelo CNPJ usando API externa.
 */
export async function consultarDadosEmpresa(
  cnpj: string
): Promise<RespostaApi> {
  const response = await fetch(
    `${API_URL}/consultas/get-dados-cnpj/${cnpj}`,
    {
      method: 'GET',
      headers: {
        'x-api-key': CNPJ_API_KEY,
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Erro ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Aprova uma solicitacao de acesso do contador usando o UUID do link.
 */
export async function aprovarSolicitacaoAcesso(
  uuid: string
): Promise<RespostaApi> {
  return fetchRespostaApi(
    `${API_URL}/api/PortalContador/AprovarSolicitacaoAcesso?uuid=${encodeURIComponent(uuid)}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );
}

/**
 * Cliente recusa a solicitação de acesso do contador.
 */
export async function negarSolicitacaoAcesso(
  uuid: string
): Promise<RespostaApi> {
  return fetchRespostaApi(
    `${API_URL}/api/PortalContador/NegarSolicitacaoAcesso?uuid=${encodeURIComponent(uuid)}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );
}

/**
 * Exclui (inativa) a conta do contador logado.
 * Requer confirmacao com a string "EXCLUIR".
 */
export async function excluirConta(
  confirmacao: string
): Promise<RespostaApi> {
  return apiClient.post('/api/PortalContador/ExcluirConta', { confirmacao });
}
